import { escapeRegExp } from 'lodash'
import { SEARCH_ORDER_PROHIBITED_COMPANIES } from 'config/search-services'
import { ReasonForDiscard } from 'config/automatic-rollovers'
import { buildEmptyValueRule } from './validationRuleBuilders'
import { buildAroValidationContext } from './aroValidationContext'

// ── Search-specific compiled patterns ─────────────────────────────────────────
const prohibitedCompaniesRe = new RegExp(
  SEARCH_ORDER_PROHIBITED_COMPANIES.map((c) => `(^${escapeRegExp(c)}$)`).join(
    '|'
  ),
  'i'
)

const ADDITIONAL_INVALID_SSN = ['123-45-6789', '111-11-1111', '333-33-3333']

const additionalInvalidSsnRegex = ADDITIONAL_INVALID_SSN.map(
  (invalidSsn) => `(^${invalidSsn}$)`
).join('|')

/**
 * Builds the Search Services validation context.
 *
 * Each field entry is a map of named rules. Every rule is a { re, error } pair:
 * - re:    RegExp compiled once at listener registration, reused across all record evaluations
 * - error: the message passed to record.addError when the rule triggers
 *
 * Format patterns (ssnFormat, dobFormat) are consumed by the pure validation
 * functions (validateSearchSsn, validateSearchDob).
 *
 * planEin, planNumber, firstName, middleName, lastName are shared with ARO — any
 * changes to those rules in aroValidationContext will automatically apply here.
 *
 * @returns {object} Validation context for search service listeners and pure validators
 */
export function buildSearchValidationContext() {
  const aro = buildAroValidationContext()
  return {
    // ── Pure validator format patterns ────────────────────────────────────────
    ssnFormat: aro.ssnFormat,
    dobFormat: /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/d/yyyy — different from ARO's yyyy/MM/dd

    // ── Field validation rules ────────────────────────────────────────────────
    planSponsorName: {
      invalidString: {
        re: /^\s*(Plan Sponsor|Plan Sponsor Name|Plan Fiduciary|Plan Fiduciary Name|Company|Company Name|Entity Name|NA|N\/A|Null)\s*$/i,
        error: 'Please type a valid plan fiduciary name',
      },
      prohibitedCompanies: {
        re: prohibitedCompaniesRe,
        error: ReasonForDiscard.PROHIBITED_COMPANY,
      },
      emptyValue: buildEmptyValueRule('Plan fiduciary'),
    },
    planName: {
      emptyValue: buildEmptyValueRule('Complete legal plan name'),
    },
    planEin: aro.planEin,
    planNumber: aro.planNumber,
    ssn: {
      validityCheck: {
        re: new RegExp(additionalInvalidSsnRegex),
        error: 'SSN is not valid',
      },
    },
    firstName: aro.firstName,
    middleName: aro.middleName,
    lastName: aro.lastName,
    address1: {
      mustContainNumberAndLetter: aro.addressLine1.mustContainNumberAndLetter,
    },
    phone: {
      phoneNumber: {
        re: /^(\+|\(|\d)[-\da-zA-Z ,.()]{2,35}$/,
        error:
          'Must start with +, (, or a number and must follow a standard formatting convention. E.g., +01 (555) 243-0321 ext. 44',
      },
    },
  }
}
